import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import { Search, Settings, Loader2, FileText, Users, Truck, X, MessageSquare } from 'lucide-react';
import { NotificationBell } from './NotificationBell';
import UserMenu from './UserMenu';
import ChatDrawer from './chat/ChatDrawer';
import AiDocketAssistantModal from './AiDocketAssistantModal';
import { Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  docket:   { label: 'Dockets',   Icon: FileText, color: 'text-accent-blue',  bg: 'bg-blue-50'   },
  customer: { label: 'Customers', Icon: Users,    color: 'text-purple-600',   bg: 'bg-purple-50' },
  trip:     { label: 'Trips',     Icon: Truck,    color: 'text-orange-600',   bg: 'bg-orange-50' },
};

const DashboardLayout = () => {
  const navigate  = useNavigate();
  const location  = useLocation();

  const [query,      setQuery]      = useState('');
  const [results,    setResults]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [open,       setOpen]       = useState(false);
  const [focusIdx,   setFocusIdx]   = useState(-1);

  const inputRef    = useRef(null);
  const [showAiDocketAssistant, setShowAiDocketAssistant] = useState(false);

  useEffect(() => {
    const handleOpenAiAssistant = () => setShowAiDocketAssistant(true);
    window.addEventListener('open-ai-docket-assistant', handleOpenAiAssistant);
    return () => window.removeEventListener('open-ai-docket-assistant', handleOpenAiAssistant);
  }, []);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        inputRef.current && !inputRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close on route change
  useEffect(() => {
    setOpen(false); setQuery('');
  }, [location.pathname]);

  // Debounced search effect
  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`${API}/api/search?q=${encodeURIComponent(query)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!cancelled) {
          setResults(data.results || []);
          setOpen(true);
          setFocusIdx(-1);
          setLoading(false);
        }
      } catch {
        if (!cancelled) { setResults([]); setLoading(false); }
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query]);

  const handleChange = (e) => { setQuery(e.target.value); };

  const handleSelect = (url) => {
    navigate(url);
    setOpen(false);
    setQuery('');
    setResults([]);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && focusIdx >= 0 && results[focusIdx]) {
      handleSelect(results[focusIdx].url);
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  // Group results by type
  const grouped = results.reduce((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {});

  // Flat index map for keyboard nav
  const flatResults = Object.values(grouped).flat();

  return (
    <div className="min-h-screen bg-bg-light">
      <Sidebar />

      {/* Main Content Area */}
      <div className="ml-[280px]">
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
          <div className="flex items-center justify-between px-8 py-4">

            {/* Search Bar */}
            <div className="flex-1 max-w-2xl relative" ref={dropdownRef}>
              <div className="relative">
                {loading
                  ? <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-accent-blue animate-spin" />
                  : <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                }
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  onFocus={() => { if (results.length > 0) setOpen(true); }}
                  placeholder="Search dockets, customers, trips..."
                  data-testid="global-search-input"
                  autoComplete="off"
                  className="w-full pl-10 pr-8 py-2.5 bg-bg-light border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent font-ibm text-sm transition-all"
                />
                {query && (
                  <button
                    onClick={() => { setQuery(''); setResults([]); setOpen(false); inputRef.current?.focus(); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Results Dropdown */}
              {open && query.length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden max-h-96 overflow-y-auto">
                  {flatResults.length === 0 && !loading ? (
                    <div className="px-4 py-6 text-center text-sm text-text-secondary font-ibm">
                      No results for &quot;<strong>{query}</strong>&quot;
                    </div>
                  ) : (
                    <>
                      {Object.entries(grouped).map(([type, items]) => {
                        const meta = TYPE_META[type];
                        return (
                          <div key={type}>
                            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">
                                {meta.label}
                              </span>
                            </div>
                            {items.map((r) => {
                              const flatIdx = flatResults.indexOf(r);
                              const isFocused = flatIdx === focusIdx;
                              return (
                                <button
                                  key={r.id}
                                  data-testid={`search-result-${r.type}-${r.id}`}
                                  onClick={() => handleSelect(r.url)}
                                  onMouseEnter={() => setFocusIdx(flatIdx)}
                                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors border-b border-gray-50 last:border-0
                                    ${isFocused ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                >
                                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${meta.bg}`}>
                                    <meta.Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                                  </div>
                                  <div className="min-w-0">
                                    <p className={`text-sm font-semibold font-ibm truncate ${isFocused ? 'text-accent-blue' : 'text-text-dark'}`}>
                                      {r.title}
                                    </p>
                                    <p className="text-xs text-text-secondary font-ibm truncate">{r.subtitle}</p>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        );
                      })}
                      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-text-secondary font-ibm flex items-center gap-1">
                        <span>{flatResults.length} result{flatResults.length !== 1 ? 's' : ''}</span>
                        <span className="ml-auto opacity-60">↑↓ navigate · Enter select · Esc close</span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-3 ml-6">
              <button
                onClick={() => setShowAiDocketAssistant(true)}
                title="AI Docket Assistant (Option B)"
                className="w-10 h-10 flex items-center justify-center rounded-lg bg-purple-50 hover:bg-purple-100 text-purple-700 transition-colors relative"
              >
                <Sparkles className="w-5 h-5 text-purple-600" />
              </button>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('open-chat'))}
                title="Team Chat"
                className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-600 transition-colors relative"
              >
                <MessageSquare className="w-5 h-5 text-slate-700" />
              </button>
              <NotificationBell />
              <button
                data-testid="settings-button"
                onClick={() => navigate('/settings')}
                title="Settings"
                className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                  location.pathname.startsWith('/settings')
                    ? 'bg-accent-blue text-white'
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                <Settings className="w-5 h-5" />
              </button>
              <UserMenu />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-8">
          <Outlet />
        </main>
      </div>
      <ChatDrawer />
      <AiDocketAssistantModal
        isOpen={showAiDocketAssistant}
        onClose={() => setShowAiDocketAssistant(false)}
      />
    </div>
  );
};

export default DashboardLayout;
